import {
  AR_RECURRING,
  FAMILY_AP,
  MORTGAGES,
  RECURRING,
} from '../../data.js';
import {
  FAMILY_DIRECT_KEY,
  MARIAN_KEY,
  calcMonthlyFamilyDirectDefault,
  calcMonthlyMarianDefault,
  effectivePHPAmount,
  recurringKey,
} from '../../engine.js';
import {
  CASH_EVENT_TYPES,
  CASH_LABELS,
  addDays,
  clampMonthDate,
  dateInRange,
  dateKey,
  monthKeysInRange,
} from './cash-monitor-model.js';

const ALL_SCENARIOS = ['pay-on-due-date', 'agreed-schedule', 'tight-budget'];
const DUE_SCENARIO = ['pay-on-due-date'];
const AGREED_SCENARIO = ['agreed-schedule'];
const TIGHT_SCENARIO = ['tight-budget'];

const SOURCE_LABEL = CASH_LABELS.SOURCE_LOCAL_DEMO;
const EXPECTED_LABELS = [CASH_LABELS.EXPECTED_RECEIPTS, CASH_LABELS.ASSUMPTION, SOURCE_LABEL];
const COMMITTED_LABELS = [CASH_LABELS.COMMITTED_OBLIGATIONS, CASH_LABELS.ASSUMPTION, SOURCE_LABEL];
const SCENARIO_COMMITTED_LABELS = [
  CASH_LABELS.COMMITTED_OBLIGATIONS,
  CASH_LABELS.ASSUMPTION,
  CASH_LABELS.SCENARIO,
  SOURCE_LABEL,
];
const OPTIONAL_LABELS = [
  CASH_LABELS.OPTIONAL_PAYMENTS,
  CASH_LABELS.ASSUMPTION,
  CASH_LABELS.SCENARIO,
  SOURCE_LABEL,
];

function cleanAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function pushIfInRange(events, event, startDate, horizonDays) {
  if (!event?.date || !dateInRange(event.date, startDate, horizonDays)) return;
  if (cleanAmount(event.amount) <= 0) return;
  events.push({
    ...event,
    date: dateKey(event.date),
    amount: cleanAmount(event.amount),
  });
}

function pushMonthlyDueEvents({
  events,
  startDate,
  horizonDays,
  line,
  lineKey,
  eventId,
  name,
  amountForMonth,
  dueDay = 1,
  tightDelayDays = 7,
  account,
}) {
  const overrideSchedule = lineKey ? line?.paymentSchedule || null : null;
  const scenarioOverride = lineKey && Object.prototype.hasOwnProperty.call(line?.scenarioOverrides || {}, lineKey)
    ? line.scenarioOverrides[lineKey]
    : undefined;
  const agreedSchedule = scenarioOverride === undefined ? overrideSchedule : scenarioOverride;

  for (const { year, month, ym } of monthKeysInRange(startDate, horizonDays)) {
    const defaultDate = clampMonthDate(year, month, dueDay);
    const amount = cleanAmount(amountForMonth(ym));
    if (amount <= 0) continue;

    pushIfInRange(events, {
      id: `${eventId}:${ym}:due`,
      date: defaultDate,
      type: CASH_EVENT_TYPES.COMMITTED_OBLIGATION,
      amount,
      title: name,
      account,
      appliesTo: DUE_SCENARIO,
      labels: SCENARIO_COMMITTED_LABELS,
      source: SOURCE_LABEL,
    }, startDate, horizonDays);

    if (!Array.isArray(agreedSchedule) || agreedSchedule.length === 0) {
      pushIfInRange(events, {
        id: `${eventId}:${ym}:agreed-default`,
        date: defaultDate,
        type: CASH_EVENT_TYPES.COMMITTED_OBLIGATION,
        amount,
        title: name,
        account,
        appliesTo: AGREED_SCENARIO,
        labels: COMMITTED_LABELS,
        source: SOURCE_LABEL,
      }, startDate, horizonDays);
    }

    const tightDate = tightDelayDays > 0 ? addDays(defaultDate, tightDelayDays) : defaultDate;
    pushIfInRange(events, {
      id: `${eventId}:${ym}:tight`,
      date: tightDate,
      type: CASH_EVENT_TYPES.COMMITTED_OBLIGATION,
      amount,
      title: `${name} - tight-budget timing`,
      account,
      appliesTo: TIGHT_SCENARIO,
      labels: SCENARIO_COMMITTED_LABELS,
      source: SOURCE_LABEL,
    }, startDate, horizonDays);
  }

  if (Array.isArray(agreedSchedule) && agreedSchedule.length > 0) {
    for (const row of agreedSchedule) {
      pushIfInRange(events, {
        id: `${eventId}:agreed:${row.date}`,
        date: row.date,
        type: CASH_EVENT_TYPES.COMMITTED_OBLIGATION,
        amount: row.amount,
        title: `${name} - agreed schedule`,
        account: row.account || account,
        appliesTo: AGREED_SCENARIO,
        labels: SCENARIO_COMMITTED_LABELS,
        source: SOURCE_LABEL,
      }, startDate, horizonDays);
    }
  }
}

function scenarioOverridesForLine(scenario, lineKey) {
  if (!lineKey) return undefined;
  if (!scenario?.piecewiseOverrides) return undefined;
  if (!Object.prototype.hasOwnProperty.call(scenario.piecewiseOverrides, lineKey)) return undefined;
  return scenario.piecewiseOverrides[lineKey];
}

function withScenarioOverride(line, scenario, lineKey) {
  return {
    ...line,
    scenarioOverrides: {
      [lineKey]: scenarioOverridesForLine(scenario, lineKey),
    },
  };
}

function buildReceiptEvents({ events, startDate, horizonDays }) {
  for (const { year, month, ym } of monthKeysInRange(startDate, horizonDays)) {
    for (const source of AR_RECURRING.educationFund || []) {
      pushIfInRange(events, {
        id: `receipt:education:${source.contactId || source.from}:${ym}`,
        date: clampMonthDate(year, month, source.collectDay || AR_RECURRING.avgCollectionDays || 7),
        type: CASH_EVENT_TYPES.EXPECTED_RECEIPT,
        amount: cleanAmount(source.amount) * cleanAmount(source.collectionRate ?? 1),
        title: `${source.from} - contribution receivable`,
        account: source.account,
        appliesTo: ALL_SCENARIOS,
        labels: EXPECTED_LABELS,
        collectionRate: source.collectionRate ?? 1,
        source: SOURCE_LABEL,
      }, startDate, horizonDays);
    }

    for (const source of AR_RECURRING.familyAllowance || []) {
      pushIfInRange(events, {
        id: `receipt:family-pool:${source.contactId || source.from}:${ym}`,
        date: clampMonthDate(year, month, source.collectDay || AR_RECURRING.avgCollectionDays || 7),
        type: CASH_EVENT_TYPES.EXPECTED_RECEIPT,
        amount: cleanAmount(source.amount) * cleanAmount(source.collectionRate ?? 1),
        title: `${source.from} - pooled contribution receivable`,
        account: source.account,
        appliesTo: ALL_SCENARIOS,
        labels: EXPECTED_LABELS,
        collectionRate: source.collectionRate ?? 1,
        source: SOURCE_LABEL,
      }, startDate, horizonDays);
    }
  }
}

function buildRecurringObligationEvents({ events, scenario, startDate, horizonDays }) {
  for (const line of RECURRING.monthly || []) {
    const key = recurringKey(line);
    pushMonthlyDueEvents({
      events,
      startDate,
      horizonDays,
      line: withScenarioOverride(line, scenario, key),
      lineKey: key,
      eventId: `obligation:recurring:${line.code}`,
      name: line.vendor,
      amountForMonth: (ym) => effectivePHPAmount(line, ym, scenario),
      dueDay: line.dueDayOfMonth || line.billDayOfMonth || 1,
      tightDelayDays: 7,
      account: line.account,
    });
  }

  for (const line of RECURRING.yearly || []) {
    const key = recurringKey(line);
    const dueMonth = Number(line.month) || 1;
    pushMonthlyDueEvents({
      events,
      startDate,
      horizonDays,
      line: withScenarioOverride(line, scenario, key),
      lineKey: key,
      eventId: `obligation:yearly:${line.code}`,
      name: line.vendor,
      amountForMonth: (ym) => Number(ym.slice(5, 7)) === dueMonth ? effectivePHPAmount(line, ym, scenario) : 0,
      dueDay: line.dueDayOfMonth || 1,
      tightDelayDays: 10,
      account: line.account,
    });
  }

  for (const line of RECURRING.quarterly || []) {
    const key = recurringKey(line);
    pushMonthlyDueEvents({
      events,
      startDate,
      horizonDays,
      line: withScenarioOverride(line, scenario, key),
      lineKey: key,
      eventId: `obligation:quarterly:${line.code}`,
      name: line.vendor,
      amountForMonth: (ym) => {
        const month = Number(ym.slice(5, 7));
        return month % 3 === 0 ? effectivePHPAmount(line, ym, scenario) : 0;
      },
      dueDay: line.dueDayOfMonth || 1,
      tightDelayDays: 10,
      account: line.account,
    });
  }
}

function buildFamilyObligationEvents({ events, scenario, startDate, horizonDays }) {
  pushMonthlyDueEvents({
    events,
    startDate,
    horizonDays,
    line: withScenarioOverride(FAMILY_AP.direct, scenario, FAMILY_DIRECT_KEY),
    lineKey: FAMILY_DIRECT_KEY,
    eventId: 'obligation:family-direct',
    name: FAMILY_AP.direct.vendor,
    amountForMonth: () => calcMonthlyFamilyDirectDefault(),
    dueDay: FAMILY_AP.direct.dueDayOfMonth || 1,
    tightDelayDays: 5,
    account: FAMILY_AP.direct.account,
  });

  pushMonthlyDueEvents({
    events,
    startDate,
    horizonDays,
    line: withScenarioOverride(FAMILY_AP.viaMarian, scenario, MARIAN_KEY),
    lineKey: MARIAN_KEY,
    eventId: 'obligation:family-routed',
    name: FAMILY_AP.viaMarian.vendor,
    amountForMonth: (ym) => calcMonthlyMarianDefault(ym),
    dueDay: FAMILY_AP.viaMarian.dueDayOfMonth || 16,
    tightDelayDays: 0,
    account: FAMILY_AP.viaMarian.account,
  });
}

function buildMortgageEvents({ events, startDate, horizonDays }) {
  for (const mortgage of MORTGAGES || []) {
    pushMonthlyDueEvents({
      events,
      startDate,
      horizonDays,
      line: mortgage,
      lineKey: null,
      eventId: `obligation:mortgage:${mortgage.name}`,
      name: `${mortgage.name} mortgage`,
      amountForMonth: () => cleanAmount(mortgage.monthlyPayment),
      dueDay: mortgage.dueDayOfMonth || 1,
      tightDelayDays: 0,
      account: mortgage.account,
    });
  }
}

function buildOptionalEvents({ events, scenario, startDate, horizonDays }) {
  const configured = Array.isArray(scenario?.cashMonitorOptionalPayments)
    ? scenario.cashMonitorOptionalPayments
    : [];
  const optionalRows = configured.length > 0
    ? configured
    : [
        {
          id: 'demo-optional-buffer-payment',
          title: 'Optional debt paydown buffer',
          amount: 2500,
          date: addDays(startDate, 21),
        },
      ];

  for (const row of optionalRows) {
    pushIfInRange(events, {
      id: `optional:${row.id || row.title || row.date}`,
      date: row.date,
      type: CASH_EVENT_TYPES.OPTIONAL_PAYMENT,
      amount: row.amount,
      title: row.title || 'Optional payment',
      account: row.account,
      appliesTo: ['pay-on-due-date', 'agreed-schedule'],
      labels: OPTIONAL_LABELS,
      source: SOURCE_LABEL,
    }, startDate, horizonDays);
  }
}

export function buildScenarioCashEvents({ scenario, startDate, horizonDays }) {
  const events = [];
  const safeStart = dateKey(startDate || new Date());
  const safeHorizon = Math.max(1, Number(horizonDays) || 45);

  buildReceiptEvents({ events, startDate: safeStart, horizonDays: safeHorizon });
  buildRecurringObligationEvents({ events, scenario, startDate: safeStart, horizonDays: safeHorizon });
  buildFamilyObligationEvents({ events, scenario, startDate: safeStart, horizonDays: safeHorizon });
  buildMortgageEvents({ events, startDate: safeStart, horizonDays: safeHorizon });
  buildOptionalEvents({ events, scenario, startDate: safeStart, horizonDays: safeHorizon });

  return events.sort((a, b) => (
    a.date.localeCompare(b.date) ||
    String(a.title || a.id).localeCompare(String(b.title || b.id))
  ));
}

export function buildScenarioCashInput({ scenario, openingCash, startDate, horizonDays, scenarioId, includeOptionalPayments, minimumCashFloor }) {
  return {
    startDate,
    horizonDays,
    openingCash,
    scenarioId,
    includeOptionalPayments,
    minimumCashFloor,
    events: buildScenarioCashEvents({ scenario, startDate, horizonDays }),
  };
}
